import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange
}) {
  if (totalPages <= 1 && !rowsPerPage) return null

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber)
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3">
      {/* Rows per page */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Rows per page:</span>
        <Select
          value={String(rowsPerPage)}
          onValueChange={(value) => {
            onRowsPerPageChange(Number(value))
            onPageChange(1) // reset to page 1 when changing rows
          }}
        >
          <SelectTrigger className="w-[80px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 20, 30, 50, 100].map((num) => (
              <SelectItem key={num} value={String(num)}>
                {num}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Page navigation */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          Prev
        </Button>

        {/* Smart pagination: show first, last, current, and pages around current */}
        {totalPages <= 7 ? (
          // Show all pages if 7 or fewer
          [...Array(totalPages)].map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })
        ) : (
          // Show smart pagination for many pages
          <>
            {/* First page */}
            <Button
              variant={currentPage === 1 ? "default" : "outline"}
              size="sm"
              onClick={() => goToPage(1)}
            >
              1
            </Button>

            {/* Ellipsis before current page range */}
            {currentPage > 3 && (
              <span className="px-2 text-gray-500">...</span>
            )}

            {/* Pages around current */}
            {(() => {
              const pages = [];
              let startPage, endPage;

              if (currentPage <= 3) {
                // Near the beginning: show pages 2-6
                startPage = 2;
                endPage = Math.min(6, totalPages - 1);
              } else if (currentPage >= totalPages - 2) {
                // Near the end: show last 5 pages before the last page
                startPage = Math.max(2, totalPages - 4);
                endPage = totalPages - 1;
              } else {
                // In the middle: show 2 pages before and after current
                startPage = currentPage - 2;
                endPage = currentPage + 2;
              }

              for (let i = startPage; i <= endPage; i++) {
                if (i > 1 && i < totalPages) {
                  pages.push(i);
                }
              }

              return pages.map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ));
            })()}

            {/* Ellipsis after current page range */}
            {currentPage < totalPages - 2 && (
              <span className="px-2 text-gray-500">...</span>
            )}

            {/* Last page */}
            <Button
              variant={currentPage === totalPages ? "default" : "outline"}
              size="sm"
              onClick={() => goToPage(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
